import { useContext, useEffect, useRef } from "react";
import { RealtimeContext } from "./RealtimeProvider";

/**
 * Multiple components (CommsNotifyProvider, InboxList, ...) subscribe to the
 * same per-user broadcast channel concurrently. Centrifuge only allows one
 * Subscription object per channel per client — a second `newSubscription`
 * call for a channel that's already subscribed throws. Share one real
 * subscription per (client, channel) pair here and fan out publications to
 * every listener; only the first caller creates it, only the last caller
 * (on unmount) tears it down.
 */
const registriesByClient = new WeakMap();

const getRegistry = (centrifuge, channel) => {
  let byChannel = registriesByClient.get(centrifuge);
  if (!byChannel) {
    byChannel = new Map();
    registriesByClient.set(centrifuge, byChannel);
  }
  let registry = byChannel.get(channel);
  if (!registry) {
    registry = { sub: null, listeners: new Set() };
    byChannel.set(channel, registry);
  }
  return registry;
};

const releaseRegistry = (centrifuge, channel, registry) => {
  if (registry.listeners.size > 0) {
    return;
  }
  registriesByClient.get(centrifuge)?.delete(channel);
  if (registry.sub) {
    try {
      registry.sub.removeAllListeners?.();
      registry.sub.unsubscribe();
    } catch (err) {
      console.error("User channel cleanup failed:", err);
    }
  }
};

/**
 * Subscribe to the current user's personal broadcast channel.
 *
 * Per the integration guide, notifications arrive on `broadcast:{user_id}` —
 * the ONE documented case where the channel name is derived client-side.
 * There is no room-style subscription token for this channel; it is authorized
 * via the connection token. If a subscription token IS required and the server
 * responds 403, we catch and no-op (best-effort, never crash).
 *
 * Payloads are discriminated by `type`, so this channel carries more than one
 * kind of event; dispatch each explicitly rather than forwarding everything,
 * so a new server-side type can never land in a handler that wasn't written
 * for it.
 *
 * @param {string|number} userId
 * @param {{ onNotification?: (data: any) => void,
 *           onBulkSend?: (data: any) => void }} handlers - `onBulkSend`
 *   receives `bulk_send_started|progress|done` for a coach's multi-select send.
 */
export function useUserChannel(userId, handlers = {}) {
  const { centrifuge, connected } = useContext(RealtimeContext);

  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!userId || !centrifuge || !connected) {
      return undefined;
    }

    const channel = `broadcast:${userId}`;
    const registry = getRegistry(centrifuge, channel);

    const listener = (data) => {
      if (!data || typeof data.type !== "string") {
        return;
      }
      if (data.type === "notification") {
        handlersRef.current.onNotification?.(data);
      } else if (data.type.startsWith("bulk_send_")) {
        handlersRef.current.onBulkSend?.(data);
      }
    };
    registry.listeners.add(listener);

    if (!registry.sub) {
      try {
        const sub = centrifuge.newSubscription(channel);
        sub.on("publication", (ctx) => {
          registry.listeners.forEach((fn) => fn(ctx.data));
        });
        sub.on("error", () => {
          // 403 / not permitted — best-effort, ignore.
        });
        sub.subscribe();
        registry.sub = sub;
      } catch (err) {
        // Subscription setup failed — degrade gracefully.
        console.error("User channel subscription failed:", err);
        registry.sub = null;
      }
    }

    return () => {
      registry.listeners.delete(listener);
      releaseRegistry(centrifuge, channel, registry);
    };
  }, [userId, centrifuge, connected]);
}
